import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ShieldCheck, Flame, ArrowRight, CheckCircle2, Users, BarChart3, Eye, Target, Star, Clock } from 'lucide-react';
import Steps from '../components/Steps';
import PricingCards from '../components/PricingCards';
import Reviews from '../components/Reviews';
import ArchiveTable from '../components/ArchiveTable';
import { PerformanceChart } from '../components/analytics/Visuals';
import { heroImages, features } from '../mock';
import { getStats, monthlyWinRateSeries } from '../data/archiveData';
import { fetchArchive, fetchSettings, fetchDailyTips, fetchPosts } from '../api';
import Seo from '../components/Seo';

const featureIcons = [Users, BarChart3, Eye, Target];

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '24BetBox',
    url: 'https://24betbox.com',
    logo: 'https://24betbox.com/og-image.jpg',
    description: 'VIP premium football predictions from a team of verified professional tipsters with a fully published win/loss archive.',
    email: '24betbox.service@gmail.com',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '24BetBox',
    url: 'https://24betbox.com',
  },
];

export default function Home() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [freeTip, setFreeTip] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, s, tips, p] = await Promise.all([fetchArchive(), fetchSettings(), fetchDailyTips(), fetchPosts()]);
        setRows(a); setSettings(s);
        const free = (tips || []).find((t) => t.tier !== 'vip');
        setFreeTip(free || null);
        setPosts((p || []).slice(0, 3));
      } catch (e) { /* keep empty */ }
    })();
  }, []);

  const stats = getStats(rows);
  const recent = rows.filter((r) => r.result && r.result !== '-').slice(0, 8);
  const series = monthlyWinRateSeries(rows);
  const val = (v, fb) => (v !== undefined && v !== null && String(v).trim() !== '') ? v : fb;

  const statCards = [
    { value: val(settings?.tipsters, '10+'), label: 'Professional Tipsters' },
    { value: val(settings?.winRate, `${stats.winRate}%`), label: 'Archived Win Rate' },
    { value: val(settings?.tipsSettled, stats.played), label: 'Tips Settled' },
    { value: val(settings?.winningTips, stats.wins), label: 'Winning Tips' },
  ];

  return (
    <div>
      <Seo
        title="24BetBox | VIP Premium Football Predictions & Betting Tips"
        description="Daily VIP football predictions from verified professional tipsters. Accurate betting tips with a transparent, fully published win/loss archive. Bet smart with 24BetBox."
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* Hero */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 lg:py-16">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-orange-400">
                <Flame className="h-3.5 w-3.5" /> The Only Genuine Tipster Team
              </span>
              <h1 className="mt-6 font-heading text-5xl font-extrabold leading-[1.05] text-white sm:text-6xl lg:text-7xl">
                {settings?.heroHeadline && settings.heroHeadline !== 'VIP Premium Football Predictions'
                  ? settings.heroHeadline
                  : <>VIP Premium <span className="orange-text-gradient">Football</span> Predictions</>}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-300">
                {val(settings?.heroSubtext, 'Data-driven predictions from a team of 10 verified professional tipsters. Every pick is backed by advanced analytics and published openly in our archive.')}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/contact" className="btn-orange inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-heading text-base font-bold uppercase tracking-wide">
                  <Trophy className="h-5 w-5" /> Get VIP Tips
                </Link>
                <Link to="/singles-archive" className="inline-flex items-center gap-2 rounded-lg border border-[#3a322a] bg-white/5 px-7 py-3.5 font-heading text-base font-bold uppercase tracking-wide text-white transition-colors hover:border-orange-500/50 hover:bg-white/10">
                  View Archive <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-400">
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Transparent results</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Daily delivery</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> 24/7 support</span>
              </div>
            </div>
            <div className="relative animate-fade-up">
              <div className="card-dark rounded-2xl p-6 shadow-2xl shadow-black/60">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-2 font-heading text-lg font-bold text-white"><BarChart3 className="h-5 w-5 text-orange-500" /> Monthly Win Rate</p>
                  <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-400">Live Archive</span>
                </div>
                <div className="mt-4"><PerformanceChart data={series} /></div>
                <div className="mt-4 grid grid-cols-3 gap-3 border-t border-[#2e2822] pt-4 text-center">
                  <div><p className="font-heading text-2xl font-extrabold orange-text-gradient">{stats.winRate}%</p><p className="text-xs text-neutral-500">Win Rate</p></div>
                  <div><p className="font-heading text-2xl font-extrabold text-emerald-400">{stats.wins}</p><p className="text-xs text-neutral-500">Wins</p></div>
                  <div><p className="font-heading text-2xl font-extrabold text-white">{stats.played}</p><p className="text-xs text-neutral-500">Settled</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-[#2e2822] bg-[#100d0a]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {statCards.map((s, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-4xl font-extrabold orange-text-gradient">{s.value}</p>
                <p className="mt-1 text-sm text-neutral-400">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Today's Free Tip teaser */}
      {freeTip && (
        <section className="bg-[#15120f] pt-12" data-testid="home-free-tip-teaser">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="relative overflow-hidden rounded-2xl border border-orange-500/40 bg-gradient-to-r from-[#241a10] via-[#1f1913] to-[#15120f] p-6 sm:p-7">
              <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-orange-500/15 blur-3xl" />
              <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-3 py-1 font-heading text-xs font-extrabold uppercase tracking-wide text-[#15120f]"><Star className="h-3.5 w-3.5" /> Today's Free Tip</span>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-neutral-400">
                    {freeTip.league && <span className="rounded-full bg-white/5 px-3 py-1 font-semibold text-neutral-300">{freeTip.league}</span>}
                    {freeTip.date && <span>{freeTip.date}</span>}
                    {freeTip.kickoff && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {freeTip.kickoff}</span>}
                  </div>
                  <h3 className="mt-2 font-heading text-2xl font-extrabold text-white sm:text-3xl">{freeTip.match}</h3>
                </div>
                <div className="flex flex-shrink-0 items-center gap-5">
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-wide text-neutral-500">Tip</p>
                    <p className="font-heading text-2xl font-extrabold text-orange-400">{freeTip.tip}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs uppercase tracking-wide text-neutral-500">Odd</p>
                    <p className="font-heading text-2xl font-extrabold text-white">{freeTip.odd}</p>
                  </div>
                  <Link to="/football-predictions-today" className="btn-orange inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-5 py-3 font-heading text-sm font-bold uppercase tracking-wide">All Tips <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Steps />

      {/* Why choose us */}
      <section className="relative overflow-hidden py-14">
        <div className="absolute inset-0">
          <img src={heroImages.tactics} alt="" className="h-full w-full object-cover opacity-[0.06]" />
          <div className="absolute inset-0 bg-[#15120f]/90" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Why 24BETBOX</p>
              <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">We Rely On Knowledge, Not Luck</h2>
              <p className="mt-4 text-neutral-300">Every single day our team commits over 10 hours of intensive research — reviewing form, team news, tactical matchups, market movement and injury updates — before releasing a selection.</p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-orange-400 hover:text-orange-300">Learn More About Us <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {features.map((f, i) => {
                const Icon = featureIcons[i];
                return (
                  <div key={i} className="card-dark rounded-xl p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><Icon className="h-6 w-6 text-orange-500" /></div>
                    <h3 className="mt-4 font-heading text-lg font-bold text-white">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-400">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <PricingCards />

      {/* Recent results */}
      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Track Record</p>
              <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Recent Single Results</h2>
            </div>
            <Link to="/singles-archive" className="inline-flex items-center gap-2 rounded-lg border border-[#3a322a] bg-white/5 px-5 py-2.5 font-heading text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-orange-500/50">Full Archive <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-8"><ArchiveTable rows={recent} /></div>
        </div>
      </section>

      <Reviews />

      {/* Latest from the Blog */}
      {posts.length > 0 && (
        <section className="bg-[#15120f] py-14" data-testid="home-blog-strip">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Insights & Guides</p>
                <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Latest From The Blog</h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 rounded-lg border border-[#3a322a] bg-white/5 px-5 py-2.5 font-heading text-sm font-bold uppercase tracking-wide text-white transition-colors hover:border-orange-500/50">View All <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid="home-blog-card"
                  className="card-dark group flex flex-col overflow-hidden rounded-2xl transition-transform duration-200 hover:-translate-y-1 hover:ring-1 hover:ring-orange-500/40"
                >
                  <div className="relative h-44 w-full overflow-hidden bg-[#241e18]">
                    {p.coverImage
                      ? <img src={p.coverImage} alt={p.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      : <div className="flex h-full w-full items-center justify-center"><BarChart3 className="h-10 w-10 text-orange-500/40" /></div>}
                    {p.category && <span className="absolute left-3 top-3 rounded-full bg-orange-500 px-3 py-1 text-[11px] font-extrabold uppercase text-[#15120f]">{p.category}</span>}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-lg font-extrabold leading-snug text-white group-hover:text-orange-400">{p.title}</h3>
                    {p.excerpt && <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-neutral-400">{p.excerpt}</p>}
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-orange-400">Read Guide <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VIP Tips banner */}
      <section className="bg-[#15120f] pb-4 pt-2">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Link to="/pricing" className="mx-auto block w-full max-w-[400px] overflow-hidden rounded-xl border border-[#2e2822] shadow-lg shadow-black/40 transition-transform duration-200 hover:scale-[1.02] hover:border-orange-500/50">
            <img
              src="https://customer-assets-lqy194kg.emergentagent.net/job_logos-42/artifacts/mzxhyko2_24betbox_vip_tips_FINAL_400x100.gif"
              alt="24BETBOX VIP Tips"
              className="block h-auto w-full"
            />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="section-fade py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="card-dark relative overflow-hidden rounded-2xl p-10 text-center">
            <div className="absolute left-1/2 top-0 h-40 w-80 -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
            <ShieldCheck className="relative mx-auto h-12 w-12 text-orange-500" />
            <h2 className="relative mt-4 font-heading text-4xl font-extrabold text-white">Ready To Bet Smart?</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-neutral-300">Join hundreds of football bettors who trust 24BETBOX for disciplined, expertly analysed predictions every single day.</p>
            <Link to="/contact" className="btn-orange relative mt-8 inline-flex items-center gap-2 rounded-lg px-8 py-4 font-heading text-lg font-bold uppercase tracking-wide"><Trophy className="h-5 w-5" /> Get Started Today</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
